import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '@/lib/api';
import { playNotificationChime } from '@/lib/notificationSound';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  Bell,
  Check,
  CheckCheck,
  FileText,
  Ticket,
  AlertCircle,
  Mail,
  X,
  CalendarClock,
  UserPlus,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { UserAvatar } from '@/components/common/UserAvatar';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'new_contract_request':
    case 'contract_approved':
    case 'contract_declined':
    case 'contract_active':
      return <FileText size={16} className="text-sky-500" />;
    case 'new_ticket':
      return <Ticket size={16} className="text-amber-500" />;
    case 'missing_info_required':
      return <AlertCircle size={16} className="text-orange-500" />;
    case 'invitation_sent':
      return <Mail size={16} className="text-emerald-500" />;
    case 'lead_follow_up_due':
      return <CalendarClock size={16} className="text-violet-500" />;
    case 'lead_assigned':
      return <UserPlus size={16} className="text-blue-500" />;
    case 'new_lead_landing':
      return <Sparkles size={16} className="text-violet-600" />;
    default:
      return <Bell size={16} className="text-slate-400" />;
  }
};

export function NotificationCenter() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Phase 27.42 — Violet lead-pulse. When a `new_lead_landing` (or a
  // freshly assigned lead) arrives via polling, pulse the bell in
  // violet for a few seconds to catch the agent's eye. Cleared either
  // by opening the popover or by the auto-timeout below.
  const [leadPulse, setLeadPulse] = useState(false);
  const pulseTimerRef = useRef(null);
  const prevCountRef = useRef(null);
  const initializedRef = useRef(false);

  const triggerLeadPulse = () => {
    setLeadPulse(true);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    // 12s ≈ two full ping cycles — long enough to catch the eye
    // without becoming annoying if the agent is heads-down.
    pulseTimerRef.current = setTimeout(() => setLeadPulse(false), 12000);
  };

  useEffect(() => () => {
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
  }, []);

  // Opening the panel is the agent acknowledging the incoming lead —
  // stop the pulse so it doesn't keep drawing attention.
  useEffect(() => {
    if (open && leadPulse) {
      setLeadPulse(false);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    }
  }, [open, leadPulse]);

  useEffect(() => {
    loadUnreadCount();
    // Poll every 30s while the tab is visible; skip while hidden to
    // save the server. When the tab comes back into focus we refresh
    // immediately so the badge is never stale.
    const tick = () => {
      if (document.visibilityState === 'visible') {
        loadUnreadCount();
      }
    };
    const interval = setInterval(tick, 30000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  const loadUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      const nextCount = response.data.count;
      // On the very first load, don't toast — we just want the badge.
      // On subsequent loads, if the count grew, surface a toast so the
      // agent knows something new arrived without a page refresh.
      if (initializedRef.current && prevCountRef.current !== null && nextCount > prevCountRef.current) {
        const delta = nextCount - prevCountRef.current;
        try {
          const listRes = await notificationAPI.list(true);
          const latest = (listRes.data || [])[0];
          const title = latest?.title || (delta === 1 ? 'New notification' : `${delta} new notifications`);
          const message = latest?.message || '';
          toast(title, {
            description: message,
            action: latest?.link_url ? {
              label: 'View',
              onClick: () => navigate(latest.link_url),
            } : undefined,
          });
          // Phase 27.38.5 — Play the opt-in chime for high-signal
          // events (new landing-page lead). The helper no-ops when the
          // preference is off, so we can call it unconditionally here.
          if (latest?.type === 'new_lead_landing' || latest?.type === 'lead_assigned') {
            playNotificationChime();
            // Phase 27.42 — Trigger the violet bell pulse so the agent
            // notices even if the toast has already faded.
            triggerLeadPulse();
          }
        } catch {
          toast(delta === 1 ? 'New notification' : `${delta} new notifications`);
        }
        // If the panel is open, refresh the list so the new one appears.
        if (open) {
          loadNotifications();
        }
      }
      prevCountRef.current = nextCount;
      initializedRef.current = true;
      setUnreadCount(nextCount);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.list();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        await notificationAPI.markRead(notification.notification_id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => 
          prev.map(n => n.notification_id === notification.notification_id ? { ...n, read: true } : n)
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
    
    if (notification.link_url) {
      setOpen(false);
      navigate(notification.link_url);
    }
  };

  const handleMarkAllRead = async () => {
    await notificationAPI.markAllRead();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    await notificationAPI.delete(notificationId);
    const wasUnread = notifications.find(n => n.notification_id === notificationId && !n.read);
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative transition-shadow ${leadPulse ? 'ring-2 ring-violet-400/60 ring-offset-1 ring-offset-transparent rounded-full' : ''}`}
          data-testid="notification-bell"
          data-lead-pulse={leadPulse ? 'true' : undefined}
        >
          {/* Phase 27.42 — Violet halo pulse ring. Rendered behind the
              bell only while `leadPulse` is true so it doesn't fight
              the icon during normal use. Two rings offset in time
              create a Tailwind `animate-ping`-style ripple. */}
          {leadPulse && (
            <>
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-violet-500/40 animate-ping"
                style={{ animationDuration: '1.6s' }}
              />
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-violet-500/30 animate-ping"
                style={{ animationDuration: '1.6s', animationDelay: '0.8s' }}
              />
            </>
          )}
          <Bell
            size={20}
            className={`relative ${leadPulse ? 'text-violet-600' : ''}`}
          />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-white text-xs flex items-center justify-center font-medium ${
              leadPulse ? 'bg-violet-600 ring-2 ring-violet-300/70' : 'bg-red-500'
            }`}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleMarkAllRead}
              className="text-xs h-7"
            >
              <CheckCheck size={14} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-80">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-slate-400">Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Bell size={32} className="mb-2" />
              <span>No notifications</span>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors relative group ${
                    !notification.read ? 'bg-sky-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {notification.actor_user_id || notification.actor_name ? (
                        // Show the acting person's avatar so agents can
                        // tell at a glance WHO triggered the alert. If
                        // the photo endpoint 404s, UserAvatar falls back
                        // to a coloured initials chip automatically.
                        <UserAvatar
                          userId={notification.actor_user_id}
                          name={notification.actor_name}
                          size="md"
                          testId={`notif-actor-${notification.notification_id}`}
                        />
                      ) : (
                        getNotificationIcon(notification.type)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {notification.message}
                      </p>
                      {notification.type === 'new_lead_landing' && notification.link_url && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification);
                          }}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-sm"
                          data-testid={`notif-cta-welcome-${notification.notification_id}`}
                        >
                          <MessageSquare size={12} />
                          Ver mensaje sugerido
                        </button>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, notification.notification_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-200 rounded"
                    >
                      <X size={14} className="text-slate-400" />
                    </button>
                  </div>
                  {!notification.read && (
                    <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-sky-500" />
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
